import { useGetPublicSoldTicketsQuery } from "../../store/api";
import {
    Box,
    Button,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { getPrimaryColorByAccountId } from "../../utils/styleConstants";
import { getContrastTextColor } from "../../utils/color";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";

interface SoldTicketsDashboardPublicProps {
    raffleId: string;
    limit: number;
    ticketQuantity: number | null;
    setTicketQuantity: (ticketQuantity: number) => void;
    setSelectedTickets: Dispatch<SetStateAction<number[] | undefined>>;
}

const MAX_SELECTION = 250;
export const SoldTicketsDashboardPublic = ({
    raffleId,
    limit,
    setTicketQuantity,
    setSelectedTickets: setSelectedTicketsHandler,
}: SoldTicketsDashboardPublicProps) => {
    const pageSize = limit === 10000 ? 1000 : limit;
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTickets, setSelectedTickets] = useState<number[]>([]);
    const [randomCount, setRandomCount] = useState<number>(2);

    const theme = useTheme();
    const accountId = useAppSelector(({ account }: RootState) => account.id);
    const { data: getSoldTicketsResponse } = useGetPublicSoldTicketsQuery(raffleId);

    const primaryColor =
        getPrimaryColorByAccountId(accountId) === "#FFFFFF"
            ? theme.palette.primary.main
            : getPrimaryColorByAccountId(accountId);

    const soldTickets = useMemo(() => new Set(getSoldTicketsResponse || []), [getSoldTicketsResponse]);

    const padLength = limit.toString().length -1;

    const availableTicketsMemo = useMemo(() => {
        const available: number[] = [];
        for (let i = 0; i < limit; i++) {
            if (!soldTickets.has(i)) {
                available.push(i);
            }
        }
        return available;
    }, [soldTickets, limit]);

    useEffect(() => {
        setTicketQuantity(selectedTickets.length);
        setSelectedTicketsHandler(selectedTickets);
    }, [selectedTickets]);

    const saveNumber = useCallback((number: number) => {
        setSelectedTickets((prevSelected) => {
            const alreadySelected = prevSelected.includes(number);
            if (alreadySelected) {
                return prevSelected.filter((t) => t !== number);
            } else if (prevSelected.length < MAX_SELECTION) {
                return [...prevSelected, number];
            }
            return prevSelected;
        });
    }, [selectedTickets.length]);

    const totalPages = Math.ceil(availableTicketsMemo.length / pageSize);

    const renderTickets = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const paged = availableTicketsMemo.slice(start, start + pageSize);

        return paged.map((i) => (
            <Grid item key={i}>
                <Ticket
                    number={i}
                    isSold={false}
                    isSelected={selectedTickets.includes(i)}
                    saveNumber={saveNumber}
                    padLength={padLength}
                    theme={theme}
                    primaryColor={primaryColor}
                />
            </Grid>
        ));
    }, [availableTicketsMemo, currentPage, pageSize, selectedTickets, saveNumber]);

    const selectRandomTickets = () => {
        const unselected = availableTicketsMemo.filter(t => !selectedTickets.includes(t));
        const remaining = MAX_SELECTION - selectedTickets.length;
        const countToSelect = Math.min(randomCount, remaining, unselected.length);
        if (countToSelect <= 0) return;
        const shuffled = [...unselected].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, countToSelect);
        setSelectedTickets((prev) => [...prev, ...selected]);
    };

    return (
        <Box width="100%">
            <Stack spacing={2}>
                <Stack direction="column" spacing={3} flexWrap="wrap">
                    <LegendItem color="white" label="Disponibles" border />
                    <LegendItem color={theme.palette.success.light} label="Seleccionados" />
                </Stack>

                <Divider />

                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <TextField
                        type="text"
                        label="Cantidad aleatoria"
                        size="small"
                        value={randomCount}
                        onChange={(e) => {
                            const numericVal = Number(e.target.value);
                            const val = Math.max(1, Math.min(limit, isNaN(numericVal) ? 0 : numericVal));
                            setRandomCount(val);
                        }}
                        sx={{ width: 150 }}
                        inputProps={{ max: limit }}
                    />
                    <Button
                        variant="outlined"
                        onClick={selectRandomTickets}
                        disabled={selectedTickets.length >= MAX_SELECTION}
                    >
                        Seleccionar aleatoriamente
                    </Button>
                    <Button
                        variant="text"
                        onClick={() => setSelectedTickets([])}
                        disabled={selectedTickets.length === 0}
                    >
                        Limpiar selección
                    </Button>
                </Box>

                {selectedTickets.length >= MAX_SELECTION && (
                    <Typography variant="body2" color="error">
                        Has alcanzado el límite máximo de {MAX_SELECTION} tickets seleccionados.
                    </Typography>
                )}

                <Box sx={{ maxHeight: 400, overflowY: "auto", mt: 2 }}>
                    <Grid container pt={2}>{renderTickets}</Grid>
                </Box>

                {limit === 10000 && totalPages > 1 && (
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            ← Anterior
                        </Button>
                        <Typography variant="body2">Página {currentPage} de {totalPages}</Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            Siguiente →
                        </Button>
                    </Box>
                )}

                {selectedTickets.length > 0 && (
                    <Box mt={3} p={2} borderRadius={2}>
                        <Typography variant="h6" mb={1}>Tus tickets:</Typography>
                        <Grid container spacing={1}>
                            {selectedTickets.map((ticket) => (
                                <Grid item key={ticket}>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 1,
                                            width: 60,
                                            textAlign: "center",
                                            borderRadius: 2,
                                            backgroundColor: primaryColor,
                                        }}
                                    >
                                        <Typography fontWeight="bold" color={getContrastTextColor(primaryColor)}>
                                            {ticket.toString().padStart(padLength, "0")}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};


const Ticket = React.memo(
    ({
        number,
        isSold,
        isSelected,
        saveNumber,
        padLength,
        theme,
        primaryColor,
    }: {
        number: number;
        isSold: boolean;
        isSelected: boolean;
        saveNumber: (number: number, isSold: boolean) => void;
        padLength: number;
        theme: any;
        primaryColor: string;
    }) => {
        let backgroundColor = theme.palette.background.paper;
        let borderColor = theme.palette.divider;
        let textColor = theme.palette.text.primary;

        if (isSelected) {
            backgroundColor = theme.palette.success.light;
            borderColor = theme.palette.success.main;
        } else if (isSold) {
            backgroundColor = '#ff6e6e';
            borderColor = '#000';
            textColor = "#fff";
        }

        return (
            <Paper
                elevation={1}
                sx={{
                    m: 0.5,
                    p: 1,
                    width: 60,
                    height: 60,
                    textAlign: "center",
                    backgroundColor,
                    border: `2px solid ${borderColor}`,
                    color: textColor,
                    cursor: isSold ? "not-allowed" : "pointer",
                    display: isSold ? 'none' : 'block',
                    borderRadius: 2,
                }}
                onClick={() => saveNumber(number, isSold)}
            >
                <Typography fontWeight="bold">
                    {number.toString().padStart(padLength, "0")}
                </Typography>
            </Paper>
        );
    }
);

const LegendItem = ({
    color,
    label,
    border = false,
}: {
    color: string;
    label: string;
    border?: boolean;
}) => (
    <Box display="flex" alignItems="center">
        <Box
            sx={{
                width: 16,
                height: 16,
                backgroundColor: color,
                border: border ? "1px solid #ccc" : "none",
                borderRadius: "4px",
                mr: 1,
            }}
        />
        <Typography variant="body2" fontWeight="bold">
            {label}
        </Typography>
    </Box>
);
